import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    this.collapsedKeys = new Set()
    this.boundPatchIncomingStream = this.patchIncomingStream.bind(this)
    document.addEventListener("turbo:before-stream-render", this.boundPatchIncomingStream)
  }

  disconnect() {
    document.removeEventListener("turbo:before-stream-render", this.boundPatchIncomingStream)
  }

  toggle(event) {
    event.preventDefault()
    const headerRow = event.currentTarget.closest("tr")
    const collapsed = headerRow.dataset.sectionCollapsed === "true"
    const key = headerRow.dataset.sectionKey

    if (collapsed) {
      this.collapsedKeys.delete(key)
      this.#expandSection(headerRow)
    } else {
      this.collapsedKeys.add(key)
      this.#collapseSection(headerRow)
    }
  }

  // Pre-patch the incoming stream's template content so the morph (or replace)
  // produces the already-collapsed DOM, avoiding a paint of the expanded state.
  patchIncomingStream(event) {
    if (this.collapsedKeys.size === 0) return

    const stream = event.target
    // Only handle full-content replacement actions; append/prepend/before/after
    // streams carry partial fragments, and pruning against them would wipe
    // collapsedKeys for sections that still exist outside the partial.
    if (!["replace", "update", "morph"].includes(stream.getAttribute("action"))) return
    const targetId = stream.getAttribute("target")
    const targetElement = targetId && this.element.querySelector(`#${CSS.escape(targetId)}`)
    if (!targetElement) return

    // Mutate templateElement.content (the live source), not templateContent —
    // the latter is a getter that returns a fresh cloneNode each access, so
    // mutations there are thrown away when the morph reads it.
    const fragment = stream.templateElement?.content
    if (!fragment || !fragment.firstElementChild) return

    // Compute the keys that will exist after the morph: headers in this.element
    // outside the target region (survive untouched) plus headers in the fragment
    // (replace whatever was inside the target). Prune collapsedKeys not in that
    // set so a section with the same key reappearing later shows expanded by
    // default rather than inheriting collapse state from a prior, unrelated batch.
    const futureKeys = new Set()
    this.element.querySelectorAll("tr[data-section-key]").forEach(row => {
      if (!targetElement.contains(row)) futureKeys.add(row.dataset.sectionKey)
    })
    const headerRows = fragment.querySelectorAll("tr[data-section-key]")
    headerRows.forEach(row => futureKeys.add(row.dataset.sectionKey))
    this.collapsedKeys.forEach(key => {
      if (!futureKeys.has(key)) this.collapsedKeys.delete(key)
    })

    headerRows.forEach(headerRow => {
      if (this.collapsedKeys.has(headerRow.dataset.sectionKey)) {
        this.#collapseSection(headerRow)
      }
    })
  }

  #collapseSection(headerRow) {
    headerRow.dataset.sectionCollapsed = "true"
    this.#setChevron(headerRow, true)
    this.#descendantRows(headerRow).forEach(row => row.classList.add("section-row-hidden"))
  }

  #expandSection(headerRow) {
    headerRow.dataset.sectionCollapsed = "false"
    this.#setChevron(headerRow, false)
    this.#descendantRows(headerRow).forEach(row => row.classList.remove("section-row-hidden"))
  }

  #setChevron(headerRow, collapsed) {
    const open = headerRow.querySelector(".section-chevron-open")
    const closed = headerRow.querySelector(".section-chevron-closed")
    if (!open || !closed) return
    open.classList.toggle("hidden", collapsed)
    closed.classList.toggle("hidden", !collapsed)
  }

  // Rows after `headerRow` until the next header at depth <= depth.
  #descendantRows(headerRow) {
    const depth = parseInt(headerRow.dataset.sectionDepth, 10)
    const rows = []
    let node = headerRow.nextElementSibling
    while (node) {
      if (node.dataset && node.dataset.sectionDepth !== undefined) {
        const otherDepth = parseInt(node.dataset.sectionDepth, 10)
        if (otherDepth <= depth) break
      }
      rows.push(node)
      node = node.nextElementSibling
    }
    return rows
  }
}
